import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Module } from "@/constants";
import { usePresignedClient } from "@/lib/file-storage/hooks";
import type { PresignedClient } from "@/lib/file-storage/types";
import {
  agreementPaperScanCompletionBody,
  type AgreementPaperConfirmationFields,
  type AgreementPaperScan,
  type AgreementPaperScanMutationResponse,
  type ReadAgreementDetail,
} from "@/schemas/agreement";
import { buildPaperScanPresignUrl } from "../utils/buildPaperScanPresignUrl";
import { useSilentAgreementRefresh } from "./useSilentAgreementRefresh";

/**
 * Odpowiedź `complete` / `complete-delete` nie przeszła walidacji. Serwer już zmienił stan skanu,
 * a cache nie da się zaktualizować w miejscu — wywołujący odświeża szczegóły (`refreshAgreement`,
 * raz po wsadzie plików, nie per plik) i pokazuje informację, nie błąd pliku, bo operacja się udała.
 */
export class PaperScanCompletionError extends Error {
  constructor() {
    super("Paper scan completion response has an unexpected shape");
    this.name = "PaperScanCompletionError";
  }
}

/** Dopisuje nowy skan albo podmienia istniejący (po `id`), nie ruszając kolejności. */
export const upsertPaperScan = (
  scans: readonly AgreementPaperScan[] | undefined,
  scan: AgreementPaperScan,
): AgreementPaperScan[] => {
  const current = scans ?? [];
  const index = current.findIndex((existing) => existing.id === scan.id);
  if (index === -1) return [...current, scan];
  return current.map((existing, position) =>
    position === index ? scan : existing,
  );
};

/**
 * Tylko czwórka rekrutera: pola HR należą do `useAgreementPaperConfirmation` (strażnik NISK-01
 * z etapu 1) i spóźniona odpowiedź `complete` nie może ich cofnąć u użytkownika z oboma uprawnieniami.
 */
const pickRecruiterFields = (fields: AgreementPaperConfirmationFields) => ({
  agreement_recruiter_paper_confirmed:
    fields.agreement_recruiter_paper_confirmed,
  agreement_recruiter_paper_confirmed_at:
    fields.agreement_recruiter_paper_confirmed_at,
  agreement_recruiter_paper_confirmed_by:
    fields.agreement_recruiter_paper_confirmed_by,
  agreement_recruiter_paper_confirmed_by_name:
    fields.agreement_recruiter_paper_confirmed_by_name,
});

/**
 * Presigned flow skanów umowy papierowej (ASMMNT-83, etap 2): klient presign/complete/
 * complete-delete dla danej umowy oraz wpisanie odpowiedzi `complete` / `complete-delete`
 * do cache szczegółów. Aktualizujemy cache w miejscu (`setQueryData`), bo invalidacja
 * szczegółów daje loader całej strony (`useAgreementLoading`); z tego samego powodu
 * `refreshAgreement` (nieświeża strona) jest cichym odświeżeniem. Lista umów jest invalidowana
 * jak w etapie 1 (kolumny potwierdzeń).
 *
 * @param agreementId ULID umowy z trasy (klucz cache szczegółów)
 * @returns klient presigned, `applyScanMutation` (body → cache) i `refreshAgreement` (nieświeża strona)
 */
export const useAgreementPaperScans = (agreementId: string) => {
  const queryClient = useQueryClient();

  const presignedClient: PresignedClient = usePresignedClient({
    presignUrl: buildPaperScanPresignUrl(agreementId, "presign"),
    completeUrl: buildPaperScanPresignUrl(agreementId, "complete"),
    completeDeleteUrl: buildPaperScanPresignUrl(agreementId, "complete-delete"),
  });

  const refreshAgreement = useSilentAgreementRefresh(agreementId);

  const applyScanMutation = useCallback(
    (completionBody: unknown): AgreementPaperScanMutationResponse => {
      const parsed = agreementPaperScanCompletionBody.safeParse(completionBody);
      if (!parsed.success) {
        // Serwer już przyjął zmianę (skan aktywny albo usunięty): zamiast zapisać śmieci do cache
        // zostawiamy je i rzucamy typowany błąd; wywołujący odświeża szczegóły, gdy skończy wsad.
        throw new PaperScanCompletionError();
      }
      const { data } = parsed.data;
      queryClient.setQueryData<ReadAgreementDetail>(
        [Module.Agreements, agreementId],
        (previous) =>
          previous
            ? {
                ...previous,
                agreement: {
                  ...previous.agreement,
                  ...pickRecruiterFields(data.agreement),
                },
                paper_scans: upsertPaperScan(previous.paper_scans, data.scan),
              }
            : previous,
      );
      // Lista umów (kolumny potwierdzeń) ma się odświeżyć; szczegóły są już zaktualizowane.
      queryClient.invalidateQueries({
        queryKey: [Module.Agreements],
        predicate: (query) => query.queryKey[1] !== agreementId,
      });
      return data;
    },
    [agreementId, queryClient],
  );

  return useMemo(
    () => ({ presignedClient, applyScanMutation, refreshAgreement }),
    [presignedClient, applyScanMutation, refreshAgreement],
  );
};
